import {
  createConsumerFromFormInfo,
  createInsuranceInfoFromForm,
  updateInsurancePrimaryFlag,
  updateOneConsumerByConsumerId,
  updateOneInsuranceByInsuranceId,
} from "@/lib/actions/market-graphql-queries";
import { getCashRatesForMultipleHrts } from "@/lib/actions/market-server-actions";
import {
  ConsumerAccountStatus,
  type GetProviderGroupToHrtsByHrtIdsQuery,
  InsuranceAccountStatus,
  ProductPageType,
} from "@/lib/graphql/generated-gql";
import { datadogLogs } from "@datadog/browser-logs";
import { getInsuredPrices } from "@frontend/lib/actions/actions";
import { getInsuranceByConsumerIdAndExcludeStatus } from "@frontend/lib/actions/v2actions";
import { ResponseStatus } from "@frontend/lib/interfaces";
import {
  type FormInsuranceData,
  type PricedTreatment,
  RequestSource,
} from "@frontend/lib/schemas/clientSchema";
import { useBoundStoreBase } from "@frontend/lib/stores/globalStore";
import type { Consumer } from "@frontend/lib/stores/slices/consumerSlice";
import type { CartItem } from "@frontend/lib/stores/slices/pricingMapSlice";
import type {
  PricingMapEntries,
  priceDataType,
} from "@frontend/lib/stores/slices/pricingMapSlice";
import type { ProductType } from "@frontend/lib/stores/slices/procedureSlice";
import type { User } from "@frontend/lib/stores/slices/userSlice";
import { isValid, parse, parseISO } from "date-fns";

const calculateInsuredPrices = async (
  consumerData: Consumer,
  insurances: FormInsuranceData[],
  procedures: number[],
  email: string,
  orgId?: number,
  npi?: string,
  providerLastName?: string,
): Promise<PricedTreatment[]> => {
  const hrtList = procedures.map((procedure) => {
    return { hrtId: procedure };
  });
  const isPendingPricesMode = useBoundStoreBase.getState().isPendingPricesMode;
  const response = await getInsuredPrices(
    consumerData.id,
    consumerData.fname,
    consumerData.lname,
    consumerData.dob,
    insurances,
    npi,
    providerLastName,
    hrtList,
    orgId,
    RequestSource.STOREFRONT,
    email,
    isPendingPricesMode, // forceGenerateInsuredPricedTreatments
  );

  if (response.responseStatus === ResponseStatus.ERROR) {
    // For OUT_OF_NETWORK errors, return empty array to allow flow to continue
    // The insurance verification logic will handle displaying the appropriate error
    if (response.error?.errorCode === "OUT_OF_NETWORK") {
      console.log("OUT_OF_NETWORK error detected, returning empty price list");
      return [];
    }
    throw new TypeError(response.error?.errorCode);
  }
  return response.data;
};

/**
 * Flushes edit to consumer / insurance
 */
export const flushConsumerEdit = async (
  user: User,
  shouldUpdateConsumer: boolean,
  consumer: Consumer,
  setConsumer: (newValue: Consumer) => void,
  insurances?: FormInsuranceData[] | null,
  oldInsurances?: FormInsuranceData[] | null, // keep track of insurances we want to switch out
  setInsurances?: (newValue: FormInsuranceData[]) => void,
  consumerFromStore?: Consumer,
) => {
  if (!user.verifiedFlag) {
    // Guest accounts do not create backend consumers / insurances immediately.
    // Wait until payment, then call createGuestAccounts
    setConsumer({ ...consumerFromStore, ...consumer });
    if (setInsurances) {
      setInsurances(insurances ? insurances : []);
    }
    return;
  }

  const oldConsumerId = consumer.id;
  let newConsumerId = consumer.id;

  if (shouldUpdateConsumer) {
    const dob = parse(consumer.dob, "MM-dd-yyyy", new Date());
    // update, do not do the replacement anymore
    if (oldConsumerId != null) {
      updateOneConsumerByConsumerId(
        oldConsumerId,
        // not updating sex or address yet as we don't declare them on storefront yet
        {
          firstName: consumer.fname,
          lastName: consumer.lname,
          dob: dob,
        },
      );
    } else {
      const orgId = user?.orgId;
      newConsumerId = (
        await createConsumerFromFormInfo(
          consumer.fname,
          consumer.lname,
          dob,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          user.id,
          ConsumerAccountStatus.Connected,
          orgId ? [orgId] : undefined,
          true, // this flow is only used for storefront, so setting this to true
        )
      )?.createOneConsumer.id;
    }

    if (!newConsumerId) {
      datadogLogs.logger.error("Consumer creation / update failed", consumer);
      // if the consumer was not created properly,
      return "Failed to create/update consumer";
    }

    setConsumer({ ...consumer, id: newConsumerId });
  } else {
    setConsumer(consumer);
  }

  // If insurance is undefined, we're in a self-pay only marketplace.
  // Don't clear the user's insurances from a different marketplace.
  if (insurances === undefined || setInsurances === undefined) {
    return;
  }

  // proceed without insurance, or insurances were 0
  // if an insurances were deleted, meaning the length would be 0, that's handled separately
  if (insurances === null || insurances.length === 0) {
    setInsurances([]);
  } else {
    // if new consumer is made, then just create insurances
    if (newConsumerId !== oldConsumerId) {
      // if a new consumer is made, then just make new insurances against new consumer
      const newInsurances: FormInsuranceData[] = [];
      const firstInsMemberId = insurances?.at(0)?.memberId;
      for (const insurance of insurances) {
        const isPrimary = insurance.memberId === firstInsMemberId;
        const newInsurance = await createInsuranceInfoFromForm(
          insurance.memberId,
          insurance.payer,
          insurance.state,
          newConsumerId,
          isPrimary,
        );
        newInsurances.push({
          ...insurance,
          id: newInsurance.createOneInsurance.id,
        });
      }
      setInsurances(newInsurances);
    } else {
      // on update consumer, or even if no updates are made, still have to update the insurances if there are differences
      await flushInsuranceEdit(
        user,
        consumer,
        insurances ?? [],
        oldInsurances ?? [],
        setInsurances,
      );
    }
  }
};

export const flushInsuranceEdit = async (
  user: User,
  consumer: Consumer,
  insurances: FormInsuranceData[],
  oldInsurances: FormInsuranceData[],
  setInsurances: (newValue: FormInsuranceData[]) => void,
  deletedInsurances?: FormInsuranceData[],
) => {
  if (!user.verifiedFlag) {
    // Guest accounts do not create backend consumers / insurances immediately.
    // Wait until payment, then call createGuestAccounts
    if (setInsurances) {
      setInsurances(insurances ? insurances : []);
    }
    return;
  }

  const insurancesToDelete = deletedInsurances ?? [];

  // A bit hacky but essentially 'old insurances' won't exist when
  // purposely forcing self pay prices (since we don't actually do deletion on this case)
  // when this happens, we need to recheck the insurances saved to the consumer and use that as ref
  // TODO: we should at some point stop overloading the insurances store with both signal of
  // we're using self pay vs they don't actually have insurances loaded, when that's done, this could be done better
  const consumerSavedInsurances =
    oldInsurances?.length > 0
      ? oldInsurances
      : (
          await getInsuranceByConsumerIdAndExcludeStatus(consumer.id, [
            InsuranceAccountStatus.Deleted,
            InsuranceAccountStatus.Replaced,
          ])
        )
          ?.sort(
            (a, b) =>
              (b.isPrimaryInsurance ? 1 : 0) - (a.isPrimaryInsurance ? 1 : 0),
          )
          .map((insurance) => {
            return {
              memberId: insurance.memberId,
              payer: insurance.payer,
              state: insurance.state ?? "",
              id: insurance.id,
            };
          });

  // basically go through each of the insurances at save, compare them with the old
  // if old is no longer used, then mark replaced.  If order changes, then update
  // primary or secondary
  // if new, then just make new
  const firstInsMemberId = insurances?.at(0)?.memberId;
  const insurancesProcessed = await Promise.all(
    insurances.map(async (insurance) => {
      const isPrimary = insurance.memberId === firstInsMemberId;
      const matchingInsurance = consumerSavedInsurances?.find(
        (oldInsurance) =>
          oldInsurance?.memberId?.toUpperCase() ===
            insurance.memberId?.toUpperCase() &&
          oldInsurance?.payer === insurance.payer &&
          oldInsurance?.state === insurance.state &&
          oldInsurance.id != null,
      );

      if (matchingInsurance == null || matchingInsurance.id == null) {
        const newInsurance = await createInsuranceInfoFromForm(
          insurance.memberId,
          insurance.payer,
          insurance.state,
          consumer.id,
          isPrimary,
        );

        return { ...insurance, id: newInsurance.createOneInsurance.id };
      }

      // in case someone deleted insurance, made
      insurancesToDelete.filter((ins) => ins.id !== matchingInsurance.id);

      const isPrimaryMatchedInsurance =
        consumerSavedInsurances?.at(0)?.memberId === matchingInsurance.memberId;

      // update the order of primary vs secondary if need be
      if (
        (isPrimary && !isPrimaryMatchedInsurance) ||
        (!isPrimary && isPrimaryMatchedInsurance)
      ) {
        await updateInsurancePrimaryFlag(matchingInsurance.id, isPrimary);
      }

      return { ...insurance, id: insurance.id };
    }),
  );

  // go through the original insurances and if they don't match the insurance Ids then update as replaced
  const insuranceIds = insurancesProcessed.map((insurance) => insurance.id);
  await Promise.all(
    consumerSavedInsurances?.map(async (oldInsurance) => {
      if (oldInsurance.id != null && !insuranceIds.includes(oldInsurance.id)) {
        await updateOneInsuranceByInsuranceId(
          oldInsurance.id,
          InsuranceAccountStatus.Replaced,
        );
      }
    }) ?? [],
  );

  // anything that was written as deleted should be marked as such.
  await markInsurancesAsDeletedByInsuranceId(
    insurancesToDelete.map((ins) => ins.id as string),
  );

  setInsurances(insurancesProcessed);
};

export const markInsurancesAsDeletedByInsuranceId = async (
  insuranceIds: string[],
) => {
  // go through each insurance Id and replace
  // this does not affect insurances added in during a session and removed before saving
  await Promise.all(
    insuranceIds?.map(async (insId) => {
      await updateOneInsuranceByInsuranceId(
        insId,
        InsuranceAccountStatus.Deleted,
      );
    }),
  );
  return;
};

export const markInsuranceAsDeleted = async (
  insurances: FormInsuranceData[],
  memberId: string,
) => {
  const insuranceId = insurances.find((x) => x.memberId === memberId)?.id;
  if (!insuranceId) {
    return;
  }

  // TODO: on basic info form and profile modal, instead of deleting things in real time,
  // do it after submit.  That way we don't have to worry about trying to update the primary/secondary
  // ordering
  const firstInsuranceInsuranceId = insurances?.at(0)?.id;
  // if the insurance we are trying to delete is the first, then we have to mark the
  // secondary as the primary (if it exists)
  if (insuranceId === firstInsuranceInsuranceId) {
    // if there is a secondary that was saved prior, then update it as the primary
    const secondaryId = insurances?.at(1)?.id;
    if (secondaryId != null) {
      await updateInsurancePrimaryFlag(secondaryId, true);
    }
  }
  // mark the original one as deleted
  await updateOneInsuranceByInsuranceId(
    insuranceId,
    InsuranceAccountStatus.Deleted,
  );
};

export enum MarketPaymentType {
  CASH = "CASH",
  INSURED = "INSURED",
}

export const switchCartPaymentType = async (
  clientSlug: string,
  priceMap: Map<number, priceDataType>,
  toPaymentType: "CASH" | "INSURED",
  cartItems: CartItem[],
  replaceCartItems: (
    itemToAdd: CartItem,
    itemToRemove: CartItem,
    organizationName: string,
  ) => void,
) => {
  for (const cartItem of cartItems) {
    if (cartItem.paymentType !== toPaymentType && !cartItem.cashOverride) {
      const priceData = priceMap.get(cartItem.hrts[0].hrtId);
      const paymentObject = priceData?.[toPaymentType];
      if (paymentObject) {
        const newCartItem: CartItem = {
          hrts: cartItem.hrts,
          paymentType: toPaymentType,
          id: paymentObject?.pricedTreatmentId,
          cashOverride: false,
          ...paymentObject,
        };
        replaceCartItems(newCartItem, cartItem, clientSlug);
      }
    }
  }
};

export const getPrices = async (
  insuredHrtList: number[],
  cashHrtList: number[],
  consumer: Consumer,
  insurances: FormInsuranceData[],
  orgId: number,
  email: string,
  isInsuranceValidated: boolean,
  isPendingPricesMode: boolean,
) => {
  let insuredPriceList: PricedTreatment[] = [];
  try {
    // Gate the INSURED fetch on eligibility: until insurance is validated we
    // skip the insured round-trip so the browse/PDP path shows CASH
    // immediately. When `isInsuranceValidated` flips true the caller re-keys
    // this query and `updatePriceMap`'s incremental merge backfills only the
    // missing INSURED slots. The CASH branch below is intentionally never
    // gated — cash must always fetch (it's the PDP's only cash source).
    //
    // Pending-prices mode (proceed-with-unverified) is the exception: those
    // orgs never flip `isInsuranceValidated` true, yet still need the insured
    // round-trip so `calculateInsuredPrices` runs with
    // `forceGenerateInsuredPricedTreatments` and the backend emits the $0
    // pending placeholder. So only skip insured when it's neither validated
    // nor pending — otherwise every row falls back to CASH and the review page
    // shows real cash prices instead of the $0 placeholder.
    insuredPriceList =
      insuredHrtList.length === 0 ||
      insurances.length === 0 ||
      (!isInsuranceValidated && !isPendingPricesMode)
        ? []
        : await calculateInsuredPrices(
            consumer,
            insurances,
            insuredHrtList,
            email,
            orgId,
          );
    console.log("insuredPriceList", insuredPriceList);
  } catch (error) {
    console.log("error", error);
    datadogLogs.logger.warn("Error getting prices", {
      error,
      insuredHrtList,
      cashHrtList,
      insurances,
      orgId,
    });
  }

  const cashPriceList =
    cashHrtList.length === 0
      ? []
      : await getCashRatesForMultipleHrts(cashHrtList);

  const allHrts = [...new Set([...insuredHrtList, ...cashHrtList])];

  const insuredPriceMap = new Map(
    insuredPriceList.map((x) => [
      x.hrtId,
      {
        price: Math.round((x.price / 100 + Number.EPSILON) * 100) / 100,
        pricedTreatmentId: x.id,
        metadata: x.metadata,
      },
    ]),
  );

  const cashPriceMap = new Map(
    cashPriceList.map((x) => [
      x.hrtId,
      {
        price: Math.round((x.price / 100 + Number.EPSILON) * 100) / 100,
        pricedTreatmentId: x.id,
        metadata: x.metadata,
      },
    ]),
  );
  const allPricesList: PricingMapEntries = {};
  for (const hrtId of allHrts) {
    allPricesList[hrtId] = {
      priceData: {
        CASH: cashPriceMap.get(hrtId) ?? undefined,
        INSURED: insuredPriceMap.get(hrtId) ?? undefined,
      },
      mode: insuredPriceMap.get(hrtId) ? "INSURED" : "CASH",
    };
  }
  return allPricesList;
};

export const updatePriceMap = async (
  hrtList: number[],
  consumer: Consumer,
  insurances: FormInsuranceData[],
  orgId: number,
  insurableHrts: Set<number>,
  currentPriceMap: PricingMapEntries,
  email: string,
  isInsuranceValidated: boolean,
  isPendingPricesMode: boolean,
) => {
  const hrtsNeedingCashPrice = hrtList.filter(
    (hrtId) => currentPriceMap[hrtId]?.priceData.CASH === undefined,
  );

  const hrtsNeedingInsuredPrice = hrtList.filter(
    (hrtId) =>
      insurableHrts.has(hrtId) &&
      currentPriceMap[hrtId]?.priceData.INSURED === undefined,
  );

  const newPriceMap = await getPrices(
    hrtsNeedingInsuredPrice,
    hrtsNeedingCashPrice,
    consumer,
    insurances,
    orgId,
    email,
    isInsuranceValidated,
    isPendingPricesMode,
  );

  const allKeys = [
    ...new Set([...Object.keys(currentPriceMap), ...Object.keys(newPriceMap)]),
  ];

  const allPricesList: PricingMapEntries = {};
  for (const hrtId of allKeys) {
    const isInsuranceAvailable = insurances.length > 0;

    const cashPrice =
      newPriceMap[hrtId]?.priceData.CASH ??
      currentPriceMap[hrtId]?.priceData.CASH;
    const insuredPrice =
      newPriceMap[hrtId]?.priceData.INSURED ??
      currentPriceMap[hrtId]?.priceData.INSURED;
    const existingMode = currentPriceMap[hrtId]?.mode;
    const existingHadInsuredOption =
      currentPriceMap[hrtId]?.priceData.INSURED !== undefined;

    // Validate that insured price has a valid price value
    const hasValidInsuredPrice =
      insuredPrice !== undefined &&
      insuredPrice.price !== undefined &&
      insuredPrice.price !== null &&
      !Number.isNaN(insuredPrice.price);

    // If insurance is not available, always use CASH mode (don't preserve old INSURED mode)
    if (!isInsuranceAvailable) {
      allPricesList[hrtId] = {
        priceData: {
          CASH: cashPrice,
        },
        mode: "CASH",
      };
      continue;
    }

    // Check if we're transitioning from CASH-only to CASH+INSURED
    // This happens when insurance is first added after browsing in CASH-only mode
    const wasOnlyCashAvailable =
      existingMode === "CASH" && !existingHadInsuredOption;
    const nowHasBothOptions =
      cashPrice && hasValidInsuredPrice && isInsuranceAvailable;

    // If transitioning from CASH-only to having both options, use defaultMode (INSURED)
    // Otherwise, preserve the user's explicit selection
    const shouldUseDefault = wasOnlyCashAvailable && nowHasBothOptions;
    const defaultMode = hasValidInsuredPrice ? "INSURED" : "CASH";
    const mode = shouldUseDefault ? defaultMode : (existingMode ?? defaultMode);
    allPricesList[hrtId] = {
      priceData: {
        CASH: cashPrice,
        INSURED: insuredPrice,
      },
      mode,
    };
  }

  return allPricesList;
};

/* Useful for determining whether to call clearInsuredPrices.
 * If consumers or insurances are passed as undefined, will skip evaluation of those.
 */
export const consumerHasChanged = (
  oldConsumer: Consumer,
  newConsumer: Consumer,
) => {
  const consumersDiffer = (a: Consumer, b: Consumer) =>
    a.fname !== b.fname || a.lname !== b.lname || a.dob !== b.dob;

  return (
    typeof oldConsumer !== typeof newConsumer ||
    (oldConsumer && newConsumer && consumersDiffer(oldConsumer, newConsumer))
  );
};

export const insuranceHasChanged = (
  oldInsurances: FormInsuranceData[] | null,
  newInsurances: FormInsuranceData[],
) => {
  const insurancesDiffer = (a: FormInsuranceData, b: FormInsuranceData) =>
    a.memberId !== b.memberId || a.payer !== b.payer || a.state !== b.state;

  return (
    typeof oldInsurances !== typeof newInsurances ||
    oldInsurances?.length !== newInsurances.length ||
    oldInsurances.some((x, i) => insurancesDiffer(x, newInsurances[i]))
  );
};

export const convertToMMddYYYY = (input?: string | Date, separator = "-") => {
  if (input == null) {
    return "";
  }
  // Function to ensure leading zeroes for day and month
  const ensureTwoDigits = (num: number) => num.toString().padStart(2, "0");

  let date: Date;

  if (typeof input === "string") {
    date = isValid(parse(input, "MM-dd-yyyy", new Date()))
      ? parse(input, "MM-dd-yyyy", new Date())
      : isValid(parse(input, "MM/dd/yyyy", new Date()))
        ? parse(input, "MM/dd/yyyy", new Date())
        : parseISO(input);
  } else {
    date = input as Date;
  }

  // Extract the parts of the date using local timezone methods
  // This prevents day shift issues when users are in different timezones
  const year = date.getFullYear(); // YYYY (local timezone)
  const month = ensureTwoDigits(date.getMonth() + 1); // MM (local timezone)
  const day = ensureTwoDigits(date.getDate()); // DD (local timezone)

  // Return the formatted date
  return `${month}${separator}${day}${separator}${year}`;
};

export const visitTypeToText = (
  visitType: string,
  product_page_type: string,
) => {
  switch (true) {
    case visitType === "INITIAL_REQUIRED":
      return "Initial Visit";
    case visitType === "DEFAULT":
      return "Follow Up";

    default:
      return "";
  }
};

/**
 * Check if within a product, if there is at least one active HRT. If yes, then display the product.  (or if product page type is link)
 * @param productItem
 * @returns
 */
export const isAnyHrtActive = (productItem: ProductType) =>
  productItem.productToHrt.some((hrt) => isHrtActive(hrt.hrt));

export const isHrtActive = (hrt) =>
  hrt.isConsumerFacing && hrt._count.activeProviderGroups > 0;

/**
 * check if a product is link type.  Fix for the time being until unpriced HRTs are an existing concept
 * @param productItem
 * @returns
 */
export const isAnyProductLinkType = (productItem: ProductType) =>
  productItem.product_page_type === ProductPageType.Link;

export const getProvidersByHrtIds = (
  data: GetProviderGroupToHrtsByHrtIdsQuery,
) =>
  data.providerGroupToHrts.flatMap((group) =>
    group.providerGroup.providerToProviderGroup.flatMap(
      (provider) => provider.provider,
    ),
  );
